import type { FontMaterialRaw } from '@meitu/xdesign-widgets';
import type { ServiceManager } from '@/framework/services/service-manager';
import { type IService } from '@/framework/shared';
import { traverseTrees } from '@/utils/tree-kit';
import { LayerType, type ILayerProps, type ITextLayerProps, type IVectorLayerProps } from '@/version-types';
import {
    detectConfiguredScripts,
    FALLBACK_COMMON_FONT_FAMILIES,
    FALLBACK_SCRIPT_FONT_SPECS,
    FontFallbackManager,
    type FontFallback,
    type FontFallbackCommon,
    type FontFallbackScript,
} from './fallback';
import { FullFontFaceManager } from './full';
import { FontLibrary } from './library';
import { FontSubsetScheduler } from './scheduler';
import { FontSubsetManager, FontSubsetStatus } from './subset';
import type { FontSubsetRequest, SubsetLoadOptions, SubsetPreparationOptions } from './types';

/**
 * 字体管理服务
 *
 * - 维护业务字体目录与字体数据查询能力；
 * - engine 使用 TTF 子集，并提供 fallback 字体能力；
 * - document 优先复用已加载的全量字体；未命中时优先使用 WOFF2，缺失则回退 TTF。
 *
 * 字体加载模型：
 * - loadSubset 只负责加载并注册指定字符所需的字体资源；
 * - 调用方负责收集完整字符，并维护图层所需的可用字体族名称映射；
 * - 读取侧按 `layer.__runtime.fontFamilyMap[fontFamily]` 获取每图层的可用字体族名称。
 */
export class FontService implements IService {
    private readonly library: FontLibrary;
    private readonly fullManager: FullFontFaceManager;
    declare private subsetManager: FontSubsetManager;
    declare private fallbackManager: FontFallbackManager;
    declare private scheduler: FontSubsetScheduler;

    /**
     * 已按当前业务字体目录解析 ID 的内置 fallback 配置。
     */
    private fallbackSpecs: FontFallback[] = [];

    constructor(readonly service: ServiceManager) {
        this.library = new FontLibrary();
        this.fullManager = new FullFontFaceManager(this.library);
    }

    onInit() {}

    onBindEvent() {
        // subsetManager 和 fallbackManager 依赖 textEngine service
        // 如果放在构造函数或者 onInit 中，textEngine 可能还未初始化
        this.subsetManager = new FontSubsetManager(this.service.textEngine, this.library);
        this.fallbackManager = new FontFallbackManager(this.service.textEngine, this.library);
        this.scheduler = new FontSubsetScheduler((fontFamily, chars, options) =>
            this.loadSubset(fontFamily, chars, options),
        );
    }

    onMount() {
        this.scheduler.mount();
    }

    onUnmount() {
        this.scheduler.unmount();
        this.fallbackSpecs = [];
        this.subsetManager.dispose();
        this.fallbackManager.dispose();
    }

    /**
     * 将字体原始数据转换为编辑器 IFont 列表
     * @param raw - 字体原始数据
     * @returns 编辑器 IFont 列表
     */
    public toFonts(raw: FontMaterialRaw[]) {
        return this.library.toFonts(raw);
    }

    /**
     * 将字体原始数据转换为组件 FontItem 列表
     * @param raw - 字体原始数据
     * @returns 组件 FontItem 列表
     */
    public toFontItems(raw: FontMaterialRaw[]) {
        return this.library.toFontItems(raw);
    }

    /**
     * 设置字体列表
     * @param fonts - 字体列表
     */
    public setRaw(fonts: FontMaterialRaw[]) {
        this.library.setRaw(fonts);
    }

    /**
     * 获取字体列表
     * @returns 字体列表
     */
    public getFonts() {
        return this.library.getFonts();
    }

    /**
     * 获取组件使用的字体列表
     * @returns 组件使用的字体列表
     */
    public getFontItems() {
        return this.library.getFontItems();
    }

    /**
     * 按字体族名称获取字体
     * @param family - 字体族名称
     * @returns 字体
     */
    public getByFamily(family: string) {
        return this.library.getByFamily(family);
    }

    /**
     * 按字体 ID 获取字体
     * @param id - 字体 ID
     * @returns 字体
     */
    public getById(id: string) {
        return this.library.getById(id);
    }

    /**
     * 同步读取已注册到文本引擎且覆盖目标字符的子集名称，供渲染直接复用
     *
     * @param fontFamily - 业务字体族名称
     * @param chars - 需要完整覆盖的字符
     * @returns 可用子集名称；未命中时返回 null，不触发加载
     */
    public getAvailableSubset(fontFamily: string, chars: string): string | null {
        return this.subsetManager.findAvailable(fontFamily, chars)?.name ?? null;
    }

    /**
     * 为指定字体与字符加载可用字体资源。
     *
     * engine 拉取并注册 TTF 子集；document 优先复用已加载的全量字体，
     * 未命中时并发拉取子集并预加载全量字体，资源格式优先 WOFF2、缺失时回退 TTF。
     *
     * @param fontFamily - 字体族名称
     * @param chars - 需要覆盖的字符
     * @param options - [可选] 注册目标，默认注册到文本引擎
     * @returns 可用字体族名称（全量字体族或子集字体族）；加载失败时返回 null
     */
    public async loadSubset(fontFamily: string, chars: string, options?: SubsetLoadOptions): Promise<string | null> {
        const registerTo = options?.registerTo ?? 'engine';
        if (registerTo === 'document') {
            const loadedFontFamily = this.fullManager.getLoadedFamily(fontFamily);
            if (loadedFontFamily) return loadedFontFamily;
            this.fullManager.preload(fontFamily);
        }

        const available = this.subsetManager.findAvailable(fontFamily, chars, registerTo);
        if (available) return available.name;

        const subset = await this.subsetManager.load(fontFamily, chars, registerTo);
        return subset?.status === FontSubsetStatus.Registered ? subset.name : null;
    }

    /**
     * 为首次渲染排队准备指定字体与完整字符集。
     *
     * 同轮、同注册目标和字体族的请求会合并，并受固定并发上限约束；字体资源策略仍由
     * {@link loadSubset} 统一处理。后续交互和显式等待场景应直接调用 `loadSubset`。
     *
     * @param fontFamily - 字体族名称。
     * @param chars - 需要覆盖的完整字符集。
     * @param options - [可选] 注册目标及调用方取消信号。
     * @returns 当前请求批次得到的可用字体族；取消或不可用时返回 null。
     */
    public prepareSubset(
        fontFamily: string,
        chars: string,
        options?: SubsetPreparationOptions,
    ): Promise<string | null> {
        return this.scheduler.schedule(fontFamily, chars, options);
    }

    /**
     * 为首次渲染批量排队准备字体子集。
     *
     * 数组中的请求会同步提交给现有调度器，使同轮、同注册目标和字体族的字符集能够合并。
     * 该入口不收集图层文字，也不维护图层字体映射。
     *
     * @param requests - 字体族及其需要覆盖的完整字符集。
     * @param options - [可选] 注册目标及调用方取消信号。
     * @returns 与请求数组按索引对应的可用字体族；取消或不可用的请求返回 null。
     */
    public prepareSubsets(
        requests: FontSubsetRequest[],
        options?: SubsetPreparationOptions,
    ): Promise<Array<string | null>> {
        return Promise.all(requests.map(({ fontFamily, chars }) => this.prepareSubset(fontFamily, chars, options)));
    }

    /**
     * 判断指定字体子集是否已注册且覆盖指定字符。
     * @param subsetName - 字体子集名称。
     * @param chars - 指定字符。
     * @returns 是否已注册且覆盖指定字符。
     */
    public hasAvailableSubset(subsetName: string | undefined, chars: string): boolean {
        return this.subsetManager.hasAvailable(subsetName, chars);
    }

    /**
     * 判断指定字体族是否已经注册为文本引擎 fallback 字体。
     * @param fontFamily - 字体族名称
     * @returns 是否已注册
     */
    public hasRegisteredFallback(fontFamily: string) {
        return this.fallbackManager.hasRegistered(fontFamily);
    }

    /**
     * 加载内置 fallback 字体。
     *
     * common 根据编辑器国际化配置选择；script 加载初始普通文字与矢量文字实际命中的配置。
     * 调用方应在初始图层首次排版前等待该请求完成。
     *
     * @param layers - 尚未插入画布的初始图层树。
     * @returns fallback 请求完成后 resolve。
     */
    public loadFallbacks(layers: ILayerProps[]): Promise<void> {
        const commonFallback = this.service.getConfig().isIntl
            ? FALLBACK_COMMON_FONT_FAMILIES.international
            : FALLBACK_COMMON_FONT_FAMILIES.domestic;

        this.fallbackSpecs = [
            { fontFamily: commonFallback, role: 'common' as const },
            ...FALLBACK_SCRIPT_FONT_SPECS.map(({ fontFamily, scripts }) => ({
                fontFamily,
                role: 'script' as const,
                scripts: [...scripts],
            })),
        ].flatMap<FontFallback>((spec) => {
            const id = this.library.getByFamily(spec.fontFamily)?.id;
            return id ? [{ ...spec, id }] : [];
        }); // 用 map 会产生 (FontFallback | undefined)[]，还要再写类型保护的 filter，这里 flatMap 更短，也直接得到 FontFallback[]

        let text = '';
        traverseTrees(layers, (layer: ILayerProps) => {
            if (layer.layerType === LayerType.TEXT_LAYER) {
                text += (layer.data as ITextLayerProps).text || '';
            }

            if (layer.layerType === LayerType.VECTOR_LAYER) {
                const textInfo = (layer.data as IVectorLayerProps).textInfo;
                if (textInfo) text += textInfo.text || '';
            }
        });

        return this.ensureFallbacks(text);
    }

    /**
     * 按给定文本请求 common 与命中的 script fallback。
     *
     * @internal 供新建文字与 Text Render 字体准备调用；初始化入口使用 `loadFallbacks`。
     * @param text - 待检测文本。
     * @returns fallback 请求完成后 resolve。
     */
    public ensureFallbacks(text: string): Promise<void> {
        const commonSpecs = this.fallbackSpecs.filter((spec): spec is FontFallbackCommon => spec.role === 'common');
        let scriptSpecs = this.fallbackSpecs.filter((spec): spec is FontFallbackScript => spec.role === 'script');

        // 检测文本中出现的、已配置 script fallback 声明的 script。
        const scripts = detectConfiguredScripts(text);
        scriptSpecs = scriptSpecs.filter((spec) => spec.scripts.some((script) => scripts.has(script)));

        return this.fallbackManager.request([...commonSpecs, ...scriptSpecs]);
    }
}
